import { decryptData } from "@/features/cryptography";
import { store } from "@/store";
import { streamSeveritiesProgress } from "@/store/services/facades/endpoints/severities";
import { SeveritiesAsset, supabaseApi } from "@/store/services/supabase";
import { dispatchWithRetries } from "@/store/services/supabase/utils";
import { PortfolioExportProgressTransformStream } from "@/tools/aggregate/portfolio-export/classes/PortfolioExportProgressTransformStream";
import { hexToArrayBuffer } from "@/utils/crypto";
import { delay } from "@/utils/promises";
import type { Progress } from "@climaterisk/facades_ts/progress_response_line";
import type { SeveritiesJobSchema } from "../types";

/**
 * Portfolio's check, but also accepts a null or missing `pending`, which the facades spec returns when the API
 * counts more finished items than requested. Otherwise such a run never completes.
 */
const isComplete = (progress: Partial<Progress> | undefined): progress is Progress =>
  !progress?.pending &&
  typeof progress?.total === "number" &&
  (progress.completed ?? 0) + (progress.errors ?? 0) >= progress.total;

/** Reads progress until the run is complete, reopening the stream whenever it drops first. */
const readProgressUntilComplete = async (
  id: string,
  itemIds: string[],
  signal: AbortSignal,
  onProgress: (progress: Progress) => void
): Promise<Progress> => {
  while (!signal.aborted) {
    try {
      const reader = (await streamSeveritiesProgress(itemIds, signal))
        .pipeThrough(new PortfolioExportProgressTransformStream())
        .getReader();

      let line = await reader.read();
      while (!line.done) {
        if (isComplete(line.value)) return line.value;
        if (typeof line.value?.total === "number") onProgress(line.value);
        line = await reader.read();
      }
    } catch (err) {
      signal.throwIfAborted();
      console.error(`Severities progress stream for ${id} failed:`, err);
    }

    await delay(5000);
  }

  throw signal.reason;
};

/**
 * Follows a posted run's progress stream until every item has completed or errored, then saves the counts.
 * Passes each update to `onProgress` on the way. Rejects with the signal's reason once aborted.
 */
export const trackSeveritiesProgress = async (
  { id, secret }: SeveritiesJobSchema,
  signal: AbortSignal,
  onProgress: (assets: SeveritiesJobSchema["assets"]) => void
) => {
  const assets = await dispatchWithRetries<Pick<SeveritiesAsset, "encrypted_id">[]>({
    action: supabaseApi.endpoints.getSeveritiesAssetIds.initiate(id, { forceRefetch: true, subscribe: false }),
    maxRetries: 5,
    timeout: 5000,
  });
  const itemIds = await Promise.all(
    assets.map(({ encrypted_id }) => decryptData(secret, hexToArrayBuffer(encrypted_id)))
  );

  const progress = await readProgressUntilComplete(id, itemIds, signal, ({ total, completed, errors, pending }) =>
    onProgress({ totalCount: total, processedCount: completed, errorCount: errors, unprocessedCount: pending ?? 0 })
  );
  // Cancelled just as it finished, so neither save nor notify.
  signal.throwIfAborted();

  // Not retried with the stream: if saving fails, the run is tracked again on the next page load.
  await dispatchWithRetries<null>({
    action: supabaseApi.endpoints.updateSeveritiesProgressCounts.initiate({
      id,
      total: progress.total,
      success: progress.completed,
      error: progress.errors,
      pending: 0,
    }),
    maxRetries: 5,
    timeout: 5000,
  });
  store.dispatch(supabaseApi.util.invalidateTags([{ type: "Org", id: "ToolUsage" }]));
};
